import { Alert, AlertIcon, Button, Stack, Text, Checkbox } from '@chakra-ui/react';
import { useState } from 'react';
import { MfaMethod } from 'types';

import TotpCodeInput from '../../../atoms/totp-code-input';
import MfaTotpIcon from '../../../atoms/mfa-totp-icon';
import MfaSmsIcon from '../../../atoms/mfa-sms-icon';
import { MFAHeader } from '../MFAHeader';
import { Title } from '../../../atoms/Title';

export interface IMfaAuth {
  isMobile?: boolean;
  method?: string;
  uniLogo: string;
  serverErrors?: string[];
  resendCountdown?: string;
  onSubmit: (code: string, rememberDevice?: boolean) => void;
  onResendCode: () => void;
  onAuthHelp: () => void;
  onSignOut?: () => void;
  showRememberDevice: boolean;
}

const getHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Use an authenticator app';
    case MfaMethod.SMS:
      return 'Send a code to a phone number';
    default:
      return '';
  }
};

const getContentHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Enter the 6-digit code found in your authenticator app';
    case MfaMethod.SMS:
      return 'Enter the 6-digit code that has been sent as a text message';
    default:
      return '';
  }
};

const getIcon = (method: string | undefined): React.ReactNode => {
  switch (method) {
    case MfaMethod.TOTP:
      return <MfaTotpIcon />;
    case MfaMethod.SMS:
      return <MfaSmsIcon />;
    default:
      return null;
  }
};

export function MfaAuthOnly({
  resendCountdown,
  method,
  serverErrors,
  onResendCode,
  onSubmit,
  onAuthHelp,
  showRememberDevice,
}: IMfaAuth) {
  const [code, setCode] = useState('');
  const headingText = getHeadingText(method);
  const contentHeading = getContentHeadingText(method);
  const icon = getIcon(method);

  const onCodeChange = (value: string) => {
    setCode(value);
  };

  // Remember device option is only available for authentication, not for MFA setup.
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    onSubmit(code, showRememberDevice ? rememberDevice : undefined);
  };

  return (
    <>
      <Title title={headingText} />

      <MFAHeader icon={icon}>{headingText}</MFAHeader>

      <Text fontWeight={'medium'} fontSize={'lg'}>
        {contentHeading}
      </Text>

      <form onSubmit={handleSubmit}>
        <TotpCodeInput onChange={onCodeChange} digits={6} value={code} />

        {serverErrors && serverErrors.length > 0 && (
          <Alert status="error" mt={6} mb={6}>
            <AlertIcon />
            The code is invalid or expired.
          </Alert>
        )}

        <Stack flexDirection={'column'} spacing={3} mt={8}>
          <Button type="submit" size={'lg'} variant={'solid'} data-selector="mfa-auth-confirm">
            Enter Code
          </Button>

          {method !== MfaMethod.TOTP && (
            <Button onClick={onResendCode} isDisabled={resendCountdown !== '00'} size={'lg'}>
              Resend code {resendCountdown !== '00' ? `in ${resendCountdown}s` : ''}
            </Button>
          )}

          <Button onClick={onAuthHelp} size={'lg'} variant={'ghost'}>
            Need help authenticating?
          </Button>

          {showRememberDevice && (
            <Checkbox
              isChecked={rememberDevice}
              onChange={e => setRememberDevice(e.target.checked)}
              data-selector="mfa-auth-remember-device"
              size={'lg'}
              variant={'outline'}
              justifyContent="center"
            >
              Remember this device
            </Checkbox>
          )}

          {showRememberDevice && (
            <Text fontSize={'sm'} color={'muted'} textAlign={'center'}>
              Trusted devices will be remembered for 30 days and will not require MFA when logging in. Do not select
              this option on shared devices.
            </Text>
          )}
        </Stack>
      </form>
    </>
  );
}
